import { RETAINER_INVOICE_MODES } from '@clockwise/shared';
import { z } from 'zod';

/**
 * The tenant's invoice settings as returned by the API.
 *
 * `null` from `get()` is a first-class state, not an error: a tenant that has
 * never configured invoicing has no issuer block, and there is no honest default
 * for a postal address or a bank account. Callers render a setup prompt.
 */
export const InvoiceSettingsSchema = z.object({
  issuerName: z.string(),
  issuerAddressLines: z.array(z.string()),
  issuerPhone: z.string().nullable(),
  issuerFax: z.string().nullable(),
  issuerEmail: z.string().nullable(),
  issuerWebsite: z.string().nullable(),
  /** Remittance lines, printed verbatim in the bottom-left box of the invoice. */
  paymentDetails: z.array(z.string()),
  /** Standard printed label, e.g. 'Net 30'; its day count is derived. */
  defaultTerms: z.string().nullable(),
  defaultCurrency: z.string(),
  /** What an invoice raised right now would be numbered, e.g. '26-08-0012'.
   *  A preview, not a reservation — the year-month segment moves with the
   *  calendar and the running segment restarts at 0001 each month. */
  nextInvoiceNumberPreview: z.string(),
  retainerInvoiceMode: z.enum(RETAINER_INVOICE_MODES),
  /** When scheduled invoices go out: this hour (0-23) in `sendTimezone`. */
  sendHour: z.number().int(),
  sendTimezone: z.string(),
  /** The invoice email body as a Handlebars template, or null for the built-in
   *  `DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE` (@clockwise/shared). */
  emailBodyTemplate: z.string().nullable(),
  /** The subject line as a template, or null for the built-in
   *  `DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE`. */
  emailSubjectTemplate: z.string().nullable(),
  /** Default accounts per line type (chart-of-accounts ids); null = none. */
  defaultRetainerAccountId: z.string().nullable(),
  defaultServiceAccountId: z.string().nullable(),
  defaultExpenseAccountId: z.string().nullable(),
  defaultDiscountAccountId: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type InvoiceSettings = z.infer<typeof InvoiceSettingsSchema>;

/** Full replace — the caller always sends every field. There is no patch
 *  endpoint, so a client that forgets a key gets a 400 rather than silently
 *  clearing the tenant's bank details. */
export const UpdateInvoiceSettingsInput = z.object({
  issuerName: z.string().trim().min(1).max(120),
  issuerAddressLines: z.array(z.string().trim().min(1).max(120)).max(6),
  issuerPhone: z.string().trim().max(40).nullable().optional(),
  issuerFax: z.string().trim().max(40).nullable().optional(),
  issuerEmail: z.string().trim().max(160).nullable().optional(),
  issuerWebsite: z.string().trim().max(160).nullable().optional(),
  paymentDetails: z.array(z.string().trim().min(1).max(120)).max(12),
  defaultTerms: z.string().trim().max(60).nullable().optional(),
  defaultCurrency: z.string().trim().length(3),
  retainerInvoiceMode: z.enum(RETAINER_INVOICE_MODES),
  sendHour: z.number().int().min(0).max(23).optional(),
  sendTimezone: z.string().optional(),
  /** Null or blank resets to the built-in default. Unknown variables are a 400. */
  emailBodyTemplate: z.string().nullable().optional(),
  /** Same rules, one line. */
  emailSubjectTemplate: z.string().nullable().optional(),
  /** Absent keeps the stored default; null clears it. */
  defaultRetainerAccountId: z.string().nullable().optional(),
  defaultServiceAccountId: z.string().nullable().optional(),
  defaultExpenseAccountId: z.string().nullable().optional(),
  defaultDiscountAccountId: z.string().nullable().optional(),
});
export type UpdateInvoiceSettingsInput = z.infer<typeof UpdateInvoiceSettingsInput>;
